/**
 * Какая функциональная зона под точкой — по цвету пикселя растрового листа.
 *
 * Лист генплана растровый: у него нет ни атрибутов, ни границ зон как объектов,
 * поэтому queryRenderedFeatures по нему ничего не вернёт. Зато цвет заливки на
 * чертеже — это и есть зона, а расшифровка цветов взята с легенды самого листа.
 *
 * Пиксель читается из НАШЕГО архива PMTiles, а не запрашивается у геопортала.
 * Причин три: работает офлайн, не грузит чужой сервер на каждый клик и — самое
 * важное — позволяет посмотреть не одну точку, а её окрестность. Последнее
 * решает главную проблему: планируемые зоны нарисованы штриховкой, и одиночный
 * пиксель с заметной вероятностью попадёт в линию штриха, а не в заливку. По
 * окрестности проводится голосование за образцы легенды — пиксели штриховки
 * далеки от любого образца и просто не голосуют (см. zoneByVotes).
 *
 * Читать пиксель из канваса карты нельзя: там уже смешаны подложка, слой и его
 * прозрачность, то есть цвет заведомо не тот, что в легенде.
 */

import type { Protocol } from "pmtiles";
import { PMTiles } from "pmtiles";
import { type GenplanZone, zoneByVotes } from "~/config/genplanZones2024";

/**
 * Стороны окон выборки в пикселях тайла, от тесного к широкому.
 *
 * Нечётные, чтобы точка была в центре. Сначала пробуется тесное окно: оно
 * точнее привязано к месту клика и не заглядывает в соседнюю зону. Если по нему
 * ответа нет, пробуется широкое — этот случай встречается на подписях чертежа,
 * где белая обводка букв занимает почти всё тесное окно и голосовать оказывается
 * нечем, хотя вокруг сплошная заливка одной зоны.
 *
 * Расширение не делает ответ менее строгим: требование перевеса втрое остаётся,
 * поэтому на настоящей границе двух зон широкое окно тоже промолчит — там оба
 * цвета наберут сопоставимо.
 */
const WINDOWS = [15, 31];

/** Размер тайла. Растровые архивы конвейера нарезаны по 256. */
const TILE_SIZE = 256;

export interface ZoneSample {
  zone: GenplanZone;
  /** Доля пикселей окна, поддержавших ответ, — для отладки и честности. */
  share: number;
}

/**
 * Что нашлось под точкой.
 *
 * Три исхода, а не два, и различать их важно. «Пусто» и «нарисовано, но не
 * опознано» раньше сливались в одно молчание, и человек не мог понять, то ли
 * на листе в этом месте ничего нет, то ли программа не справилась. Второе
 * честно называется своим именем и отсылает к легенде.
 */
export type ZoneLookup =
  | { kind: "zone"; zone: GenplanZone; share: number }
  | { kind: "unknown" }
  | null;

/**
 * Насколько цветным должен быть пиксель, чтобы счесть его обозначением.
 *
 * Считается не любая краска, а именно цветная. На каждом листе под тематикой
 * лежит общий чертёж города: серые контуры зданий, линии улиц, подписи. Если
 * принимать за обозначение всякий небелый пиксель, подсказка станет выскакивать
 * в любой точке города и будет утверждать, что здесь что-то обозначено, тогда
 * как под курсором обычная подложка. Серое имеет малый разброс между каналами,
 * цветное — заметный.
 *
 * Плата за это честная и её стоит назвать: чёрные пунктиры границ по такому
 * правилу не считаются обозначением. Промолчать о существующем хуже, чем
 * заявить о несуществующем, но не настолько, чтобы менять размен.
 */
const COLOUR_THRESHOLD = 28;

/** Совсем светлое — это поле листа, даже если оно чуть тонировано. */
const WHITE_THRESHOLD = 246;

/**
 * Какая доля окна должна быть занята цветным обозначением.
 *
 * Одинокие пиксели — сглаживание подписей и мусор сканирования. Десятая часть
 * окна означает, что под курсором действительно что-то нарисовано.
 */
const INK_SHARE = 0.1;

/**
 * Сколько разобранных тайлов держать в памяти.
 *
 * Подсказка по наведению спрашивает зону при каждом движении курсора, и без
 * кэша это означало бы сетевой запрос и раскодирование WEBP по нескольку раз в
 * секунду. Тайл 256x256 в RGBA — четверть мегабайта, дюжина укладывается в три.
 */
const TILE_CACHE_LIMIT = 12;

/** Архивы, уже открытые для чтения пикселей: повторно индекс не перечитываем. */
const archives = new Map<string, PMTiles>();

/**
 * Разобранные тайлы. Значение null означает «тайла здесь нет» — это тоже надо
 * помнить, иначе за пределами листа кэш не работает вовсе.
 */
const tiles = new Map<string, ImageData | null>();

function archiveFor(protocol: Protocol, tileUrl: string): PMTiles {
  // Если карта уже рисовала этот слой, экземпляр с прочитанным индексом лежит
  // в протоколе — берём его, чтобы не открывать архив второй раз. Для офлайна
  // это существенно: там в протокол положен источник поверх локального Blob.
  const shared = protocol.get(tileUrl);
  if (shared) return shared;

  let own = archives.get(tileUrl);
  if (!own) {
    own = new PMTiles(tileUrl);
    archives.set(tileUrl, own);
  }
  return own;
}

/** Тайл и позиция внутри него для географической точки. */
function tileAddress(
  lngLat: { lng: number; lat: number },
  zoom: number,
): { z: number; x: number; y: number; px: number; py: number } {
  const z = Math.max(0, Math.floor(zoom));
  const n = 2 ** z;
  const latRad = (lngLat.lat * Math.PI) / 180;

  const fx = ((lngLat.lng + 180) / 360) * n;
  const fy = ((1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2) * n;

  const x = Math.floor(fx);
  const y = Math.floor(fy);
  return {
    z,
    x,
    y,
    px: Math.min(TILE_SIZE - 1, Math.floor((fx - x) * TILE_SIZE)),
    py: Math.min(TILE_SIZE - 1, Math.floor((fy - y) * TILE_SIZE)),
  };
}

/** Тайл, разобранный в пиксели. null — тайла нет либо он не читается. */
async function tileImage(
  protocol: Protocol,
  tileUrl: string,
  z: number,
  x: number,
  y: number,
): Promise<ImageData | null> {
  const key = `${tileUrl}|${z}/${x}/${y}`;
  const cached = tiles.get(key);
  if (cached !== undefined) return cached;

  let result: ImageData | null = null;
  const tile = await archiveFor(protocol, tileUrl).getZxy(z, x, y);

  if (tile?.data && tile.data.byteLength > 0) {
    let bitmap: ImageBitmap | null = null;
    try {
      bitmap = await createImageBitmap(new Blob([tile.data]));
      const canvas = new OffscreenCanvas(bitmap.width, bitmap.height);
      const context = canvas.getContext("2d", { willReadFrequently: true });
      if (context) {
        context.drawImage(bitmap, 0, 0);
        result = context.getImageData(0, 0, bitmap.width, bitmap.height);
      }
    } catch {
      result = null; // неизвестный браузеру формат тайла — не повод ронять карту
    } finally {
      bitmap?.close();
    }
  }

  // Выселяем самый давний: Map хранит порядок вставки, поэтому первый ключ —
  // и есть самый старый.
  if (tiles.size >= TILE_CACHE_LIMIT) {
    const oldest = tiles.keys().next();
    if (!oldest.done) tiles.delete(oldest.value);
  }
  tiles.set(key, result);
  return result;
}

/** Прямоугольное окно из разобранного тайла, как его вернул бы getImageData. */
function cutWindow(image: ImageData, left: number, top: number, size: number): Uint8ClampedArray {
  const out = new Uint8ClampedArray(size * size * 4);
  for (let row = 0; row < size; row += 1) {
    const from = ((top + row) * image.width + left) * 4;
    out.set(image.data.subarray(from, from + size * 4), row * size * 4);
  }
  return out;
}

export interface ZoneLookupOptions {
  protocol: Protocol;
  /** Адрес архива PMTiles — ровно тот, что в манифесте. */
  tileUrl: string;
  maxzoom: number;
  lngLat: { lng: number; lat: number };
  /**
   * Таблица обозначений этого листа. Её может не быть: у большинства листов
   * обозначения — линии и значки, по цвету не опознаваемые. Такой лист всё
   * равно опрашивается, но только на вопрос «нарисовано ли тут что-нибудь».
   */
  zones?: readonly GenplanZone[];
}

/**
 * Зона под точкой либо null, если определить её нельзя.
 *
 * null здесь — законный ответ, а не сбой: точка может попасть за пределы
 * листа, на границу двух зон, на подпись или на цвет, которого нет в легенде.
 * Соврать названием зоны на градостроительном документе хуже, чем промолчать.
 */
export async function zoneAtPoint(options: ZoneLookupOptions): Promise<ZoneLookup> {
  const { protocol, tileUrl, maxzoom, lngLat, zones } = options;

  // Выборка идёт по максимальному зуму архива независимо от того, как далеко
  // отодвинута карта. Нижние зумы получены усреднением, то есть их пиксели —
  // это смеси цветов соседних зон: голосование по ним либо ничего не находит,
  // либо находит зону, которой под точкой нет. Вдобавок на малом зуме окно
  // 15x15 покрывало бы километры вместо двух десятков метров.
  const { z, x, y, px, py } = tileAddress(lngLat, maxzoom);

  const image = await tileImage(protocol, tileUrl, z, x, y);
  if (!image) return null;

  // Тайл может быть крупнее 256 px (масштаб @2x), поэтому позиция внутри него
  // пересчитывается из доли, а не берётся в пикселях напрямую.
  const scale = image.width / TILE_SIZE;
  const cx = Math.round((px + 0.5) * scale);
  const cy = Math.round((py + 0.5) * scale);

  let widest: Uint8ClampedArray | null = null;

  for (const size of WINDOWS) {
    if (size > image.width || size > image.height) continue;
    const half = Math.floor(size / 2);
    const left = Math.max(0, Math.min(image.width - size, cx - half));
    const top = Math.max(0, Math.min(image.height - size, cy - half));

    const window = cutWindow(image, left, top, size);
    widest = window;

    if (zones) {
      const vote = zoneByVotes(window, zones);
      if (vote) return { kind: "zone", zone: vote.zone, share: vote.share };
    }
  }

  // Опознать не удалось — но, может быть, тут просто ничего не нарисовано.
  // Проверяем по самому широкому из просмотренных окон: на узком одиночный
  // штрих даёт слишком мало пикселей, чтобы судить.
  return widest && hasColouredMark(widest) ? { kind: "unknown" } : null;
}

/** Есть ли под точкой цветное обозначение, а не подложечный чертёж. */
function hasColouredMark(window: Uint8ClampedArray): boolean {
  let coloured = 0;
  let opaque = 0;

  for (let i = 0; i < window.length; i += 4) {
    // Прозрачное — это вырез за границей округа, а не поле листа.
    if ((window[i + 3] ?? 0) < 200) continue;
    opaque += 1;

    const r = window[i] ?? 255;
    const g = window[i + 1] ?? 255;
    const b = window[i + 2] ?? 255;
    if (r > WHITE_THRESHOLD && g > WHITE_THRESHOLD && b > WHITE_THRESHOLD) continue;
    if (Math.max(r, g, b) - Math.min(r, g, b) >= COLOUR_THRESHOLD) coloured += 1;
  }

  return opaque > 0 && coloured / opaque >= INK_SHARE;
}
